import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { AxiosError } from "axios";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import RequestsPagePathParams from "../../../../../api/models/test-request/RequestsPagePathParams";
import ReusedTestSeriesApi from "../../../../../api/services/ReusedTestSeriesApi";
import TestRequestApi from "../../../../../api/services/TestRequestApi";
import TestSeriesApi from "../../../../../api/services/TestSeriesApi";
import CustomProgress from "../../../../../components/Alert/CustomProgress";
import ConfirmationDialogContext from "../../../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ErrorSnackbarContext from "../../../../../contexts/ErrorSnackbarContext";
import { TestRequestContext } from "../../../../../contexts/TestRequestContext";
import { TestSeriesContext } from "../../../../../contexts/TestSeriesContext";
import useEnv from "../../../../../hooks/env";
import ValidatableTestCase, {
  initValidatableTestCases,
  validateTestCases,
} from "../../../models/ValidatableTestCase.model";
import TestCasesOverviewTable, { TestCasesOverviewTableRef } from "./TestCasesOverviewTable";
import InvalidIdsExceptionResponse from "../../../../../api/models/test-series/InvalidIdsExceptionResponse";
import { AnalyzeHistoricalFormType } from "../../AnalyzeHistoricalForm";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minWidth: "90vw",
    },
    header: {
      marginBottom: theme.spacing(5),
    },
  }),
);

interface TestCasesTabProps {
  refreshOverviewTable: () => void;
  onClose: () => void;
  testCasesOverviewTableRef: (ref: TestCasesOverviewTableRef) => void;
  testSeriesVersion: number;
  historicalSearchSettings: AnalyzeHistoricalFormType | null;
}

const TestCasesTab: React.FC<TestCasesTabProps> = (props) => {
  const { refreshOverviewTable, onClose, testCasesOverviewTableRef, testSeriesVersion, historicalSearchSettings } =
    props;

  const { api } = useEnv();
  const testSeriesApi = new TestSeriesApi(api);
  const testRequestApi = new TestRequestApi(api);
  const reusedTestSeriesApi = new ReusedTestSeriesApi(api);

  const { testRequestId } = useParams<RequestsPagePathParams>();

  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { testRequestObj, updateTestRequestObj } = useContext(TestRequestContext);
  const {
    selectedTestSeriesId,
    updateSelectedTestSeriesObject,
    selectedTestSeriesObject,
    testSeries,
    testSeriesIsReused,
  } = useContext(TestSeriesContext);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [testCases, setTestCases] = useState<ValidatableTestCase[] | null>(null);

  const isMounted = useRef(false);

  const classes = useStyles(props);
  const updateTestSeries =
    (selectedTestSeriesId && selectedTestSeriesId !== selectedTestSeriesObject.id) ||
    testSeriesVersion !== selectedTestSeriesObject.version;
  const testSeriesRequest = testSeriesIsReused
    ? () => reusedTestSeriesApi.fetchReusedTestSeries(testRequestId, selectedTestSeriesId as number)
    : () => testSeriesApi.fetchTestSeriesVersionById(selectedTestSeriesId as number, testSeriesVersion);
  const testCasesRequest = testSeriesIsReused
    ? () => reusedTestSeriesApi.fetchReusedTestSeriesTestCases(testRequestId, selectedTestSeriesId as number)
    : () => testRequestApi.getTestCasesByIdAndVersion(selectedTestSeriesId as number, testSeriesVersion);

  const fetchTestSeriesApiData = (): void => {
    if (testCases) {
      setIsLoading(true);
    }
    if (updateTestSeries) {
      setIsLoading(true);
      testSeriesRequest()
        .then(({ data: testSeriesData }) =>
          testCasesRequest().then(({ data: testCasesData }) => {
            if (testCasesData) {
              updateSelectedTestSeriesObject(testSeriesData);
              setTestCases(initValidatableTestCases(testCasesData));
            }
          }),
        )
        .catch(handleHttpError)
        .finally(() => setIsLoading(false));
    } else if (testSeries) {
      setIsLoading(true);
      testCasesRequest()
        .then(({ data }) => {
          setTestCases(initValidatableTestCases(data));
        })
        .catch(handleHttpError)
        .finally(() => setIsLoading(false));
    }
  };

  const refreshTestCases = () => {
    testCasesRequest()
      .then(({ data }) => setTestCases(initValidatableTestCases(data)))
      .catch(handleHttpError);
  };

  const startExecution = (id: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!selectedTestSeriesObject) {
        reject(new Error("No selected test series object."));
        return;
      }
      testRequestApi
        .updateTestSeriesToExecute(id)
        .then((res) => {
          updateTestRequestObj({
            ...testRequestObj,
            state: "IN_EXECUTION",
            status: {
              label: "In Execution",
              state: "IN_EXECUTION",
            },
          });
          selectedTestSeriesObject.testSeriesStatus = "IN_EXECUTION";
          res.data.testSeriesStatus = "IN_EXECUTION";
          updateSelectedTestSeriesObject(res.data);
          refreshTestCases();
          refreshOverviewTable();
          resolve();
        })
        .catch((error) => {
          if (error?.response?.data?.errorCode === "SAMPLES_NOT_EXISTING") {
            showConfirmationDialog({
              headline: "Execution not possible",
              confirmText: "Okay",
              text: `This test series contains substrate or product samples not listed for the test 
                     request. Please correct this before executing the test series.`,
              hideCancel: true,
            })
              .then(() => {
                reject(error);
              })
              .catch(() => {
                reject(error);
              });
          } else {
            handleHttpError(error);
            reject(error);
          }
        });
    });
  };

  const checkComplete = (id: number): Promise<boolean> => {
    if (selectedTestSeriesObject && testCases) {
      return testRequestApi
        .checkIfTestSeriesIsCompletable(id)
        .then(() => false)
        .catch((error: AxiosError<InvalidIdsExceptionResponse>) => {
          if (error?.response?.data.type === "INCOMPLETE_TEST_CASES") {
            const invalidIds: number[] = error?.response?.data?.invalidIds || [];
            setTestCases((prev) => prev && validateTestCases(prev, (vtc) => invalidIds.indexOf(vtc.id) >= 0));
          }
          if (error?.response?.data.type === "UNRATED_TEST_CASES") {
            return true;
          }
          throw error;
        });
    }
    return Promise.reject(new Error(""));
  };

  const completeExecution = (id: number, ignoreUnratedTestCases: boolean): void => {
    if (selectedTestSeriesObject && testCases) {
      setIsLoading(true);
      testRequestApi
        .updateTestSeriesToComplete(id, ignoreUnratedTestCases)
        .then((response) => {
          selectedTestSeriesObject.testSeriesStatus = "IN_EXECUTION";
          updateSelectedTestSeriesObject(response.data);
          setIsLoading(false);
          refreshOverviewTable();
        })
        .catch((error: AxiosError<InvalidIdsExceptionResponse>) => {
          handleHttpError(error);
          const invalidIds: number[] = error?.response?.data?.invalidIds || [];
          setTestCases((prev) => prev && validateTestCases(prev, (vtc) => invalidIds.indexOf(vtc.id) >= 0));
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (isMounted.current) {
      fetchTestSeriesApiData();
    }
  }, [testSeriesVersion]);

  useEffect(() => {
    fetchTestSeriesApiData();
    isMounted.current = true;
  }, []);

  return (
    <TestSeriesContext.Consumer>
      {({ originRequestId }): JSX.Element => {
        return !isLoading && testCases?.length ? (
          <TestCasesOverviewTable
            ref={testCasesOverviewTableRef}
            onClose={onClose}
            testSeriesObject={selectedTestSeriesObject}
            testCases={testCases}
            testRequestId={originRequestId || testRequestId}
            refreshTestCases={refreshTestCases}
            startExecution={startExecution}
            checkComplete={checkComplete}
            completeExecution={completeExecution}
            fetchTestSeriesApiData={fetchTestSeriesApiData}
            historicalSearchSettings={historicalSearchSettings}
          />
        ) : (
          <div className={classes.root}>
            <CustomProgress />
          </div>
        );
      }}
    </TestSeriesContext.Consumer>
  );
};

export default TestCasesTab;
